'use client';
import { IC_DESC } from '@/assets/icons';
import ReadMore from '@/components/molecules/read-more';
import { slugify } from '@/services/ecommerce/utils/helper';
import { generateProductTitle } from '@/services/ecommerce/utils/MetaGenerator';
import { every, isArray, isEmpty, isObject, values } from 'lodash';
import { useSearchParams } from 'next/navigation';
import styles from './product.module.scss';
import { ProductDescription } from './ProductDescription';
import { ProductSwitcher } from './ProductSwitcher';

const ProductOverview = ({ product, data, name, strength, translationBn, p_form }: any) => {
	const search = useSearchParams();
	const wordLimit = 100;

	const isDescriptionEmpty = (obj: any) => every(values(obj), isEmpty);
	const product_name = generateProductTitle(product);
	const getLanguage = search.get('lang');
	if (product && product?.p_description?.html) {
		return (
			// <div className='text-14 fw-400 text-grey700' style={{ lineHeight: '24px' }}>
			// 	<ReadMore word={100}>{product?.p_description?.html}</ReadMore>
			// </div>
			<ProductDescription data={product} />
		);
	}

	if (!product || isDescriptionEmpty(product?.p_description?.generic || {})) return null;

	const {
		overview,
		g_overview_bn,
		quick_tips,
		g_quick_tips_bn,
		brief_description,
		g_brief_description_bn,
		safety_advices
	} = data;

	const renderContent = (item: any, name: string, strength: string, p_form: string, translationBn: any) => {
		return item
			.replace(/__NAME__/gi, translationBn?.bn?.p_name || name)
			.replace(/__STRENGTH__/gi, translationBn?.bn?.p_strength || strength)
			.replace(/__FORM__/gi, p_form);
	};
	const renderSection = (items: any[], name: string, strength: string, p_form: string, translationBn: any) => {
		return items.map((item, index) => (
			<div
				key={index}
				className={`d-flex flex-column mb-10 focus_title-${slugify(item.title.replace(/__NAME__/gi, translationBn ? translationBn?.bn?.p_name : name))}`}
				id={slugify(item.title.replace(/__NAME__/gi, translationBn ? translationBn?.bn?.p_name : name))}>
				<div className='text-16 text-primary fw-500 mb-10'>
					{renderContent(item.title, name, strength, p_form, translationBn)}
				</div>
				{isObject(item.content) ? (
					<div>
						{Object.keys(item.content).map((key) => (
							<div key={key} className='d-flex'>
								{key === 'tag' && <span className='fw-500'>{item.content[key]}</span>}
								{isArray(item.content[key]) && (
									<ul className='text-14 text-grey700 fw-400'>
										{item.content[key].map((li: any, i: number) => (
											<li className='mb-5' key={i}>
												{renderContent(li, name, strength, p_form, translationBn)}
											</li>
										))}
									</ul>
								)}
							</div>
						))}
					</div>
				) : (
					<div className='text-14 fw-400 text-grey700' style={{ lineHeight: '24px' }}>
						<ReadMore word={wordLimit}>
							{renderContent(item.content, name, strength, p_form, translationBn)}
						</ReadMore>
					</div>
				)}
			</div>
		));
	};

	try {
		return (
			<div
				className={`bg-white rounded-10 mt-20 border-1-grey300 ${styles.product_overview}`}
				style={{ position: 'relative', zIndex: 0, overflow: 'hidden' }}>
				<div className='d-flex justify-space-between align-items-center p-20'>
					<div className='text-18 text-primary fw-500 d-flex align-items-center flex-grow-1'>
						<IC_DESC />
						<h2 className='ml-10'> Medicine Overview of {translationBn?.bn?.p_name || product_name}</h2>
					</div>
					{!safety_advices?.length && <ProductSwitcher />}
				</div>

				<div className='d-flex flex-column px-20'>
					{getLanguage === 'bn' && g_overview_bn?.length
						? renderSection(g_overview_bn, name, strength, p_form, translationBn)
						: renderSection(overview, name, strength, p_form, null)}
				</div>

				{getLanguage === 'bn' && g_quick_tips_bn?.length ? (
					<div className={`d-flex flex-column mb-10 p-10 px-20 ${styles.responsiveTableWrapper}`}>
						<div className='d-flex justify-space-between'>
							<div className='text-18 text-primary fw-500 d-flex'>
								<IC_DESC /> <span className='ml-5'>Quick Tips</span>
							</div>
						</div>
						{g_quick_tips_bn.map((item: any, index: number) => (
							<ul key={index} className='text-14 text-grey900 fw-400'>
								<li style={{ listStyle: 'disc' }}>
									<ReadMore word={wordLimit}>
										{renderContent(item, name, strength, p_form, translationBn)}
									</ReadMore>
								</li>
							</ul>
						))}
					</div>
				) : quick_tips?.length ? (
					<div className='d-flex flex-column mb-10 p-10 px-20'>
						<div className='d-flex justify-space-between'>
							<div className='text-18 text-primary fw-500 d-flex'>
								<IC_DESC /> <span className='ml-5'>Quick Tips</span>
							</div>
						</div>
						{quick_tips.map((item: any, index: number) => (
							<ul key={index} className='text-14 text-grey900 fw-400'>
								<li>
									<ReadMore word={wordLimit}>{renderContent(item, name, strength, p_form, null)}</ReadMore>
								</li>
							</ul>
						))}
					</div>
				) : (
					''
				)}

				{getLanguage === 'bn' && g_brief_description_bn?.length ? (
					<div className='d-flex flex-column mb-10 p-10 px-20'>
						<div className='d-flex justify-space-between mb-20'>
							<div className='text-18 text-primary fw-500 d-flex'>
								<IC_DESC /> <span className='ml-5'> Brief Description</span>
							</div>
						</div>
						{g_brief_description_bn.map((item: any, index: number) => (
							<div
								key={index}
								className={`d-flex flex-column mb-10 focus_title-${slugify(item.title.replace(/__NAME__/gi, translationBn ? translationBn?.bn?.p_name : name))}`}
								id={slugify(
									item.title.replace(/__NAME__/gi, translationBn ? translationBn?.bn?.p_name : name)
								)}>
								<div className='text-18 text-primary fw-500 mb-20'>
									{renderContent(item.title, name, strength, p_form, translationBn)}
								</div>
								<div className='text-14 fw-400 text-grey700' style={{ lineHeight: '24px' }}>
									<ReadMore word={wordLimit}>
										{renderContent(item.content, name, strength, p_form, translationBn)}
									</ReadMore>
								</div>
							</div>
						))}
					</div>
				) : brief_description?.length ? (
					<div className={`d-flex flex-column mb-10 p-10 px-20 `}>
						<div className='d-flex justify-space-between mb-20'>
							<div className='text-18 text-primary fw-500 d-flex'>
								<IC_DESC /> <span className='ml-5'> Brief Description</span>
							</div>
						</div>
						{brief_description.map((item: any, index: number) => (
							<div
								key={index}
								className={`d-flex flex-column mb-10 focus_title-${slugify(item.title.replace(/__NAME__/gi, translationBn ? translationBn?.bn?.p_name : name))}`}
								id={slugify(
									item.title.replace(/__NAME__/gi, translationBn ? translationBn?.bn?.p_name : name)
								)}>
								<div className='text-18 text-primary fw-500 mb-10'>
									{renderContent(item.title, name, strength, p_form, null)}
								</div>
								<div className={`text-14 fw-400 text-grey700`} style={{ lineHeight: '24px' }}>
									<ReadMore word={wordLimit}>
										{renderContent(item.content, name, strength, p_form, null)}
									</ReadMore>
								</div>
							</div>
						))}
					</div>
				) : (
					''
				)}
			</div>
		);
	} catch (error) {
		console.error('Error rendering ProductOverview:', error);
		return <div className='text-danger'>Failed to load product details. Please try again later.</div>;
	}
};

export default ProductOverview;
